/**
 * @fileoverview Registry of all auditable collection identifiers.
 * @module audit/collections
 *
 * Responsibilities:
 * - Define canonical collection IDs for audit entries
 * - Provide alias resolution for collections with alternate names
 * - Serve as the single source of truth for what collections are auditable
 *
 * Dependencies:
 * - None (pure data)
 *
 * Architecture:
 * - Used by both frontend (writeAuditLog) and collection validation
 * - Collection IDs here must match the string used in audit_logs entries
 *
 * Notes:
 * - Add new collections here BEFORE adding audit to their services
 * - Aliases map sync collection names to canonical audit collection names
 * - Collections NOT in this map are either exempt or not yet audited
 */

/**
 * @contract AUDIT_COLLECTIONS
 * Purpose: Canonical map of all auditable collection identifiers in the system.
 * Fields: Key = programmatic name, Value = string used in audit_logs.collection field
 * Rules: Every collection that produces audit entries MUST be listed here.
 * Sync: N/A — compile-time constant
 */
export const AUDIT_COLLECTIONS = {
  // --- Tenant domain collections ---
  entities: 'entities',
  contacts: 'contacts',
  cases: 'cases',
  inspectionAccounts: 'inspectionAccounts',
  inspectionAccountGroups: 'inspectionAccountGroups',
  roles: 'roles',
  users: 'users',
  lookups: 'lookups',
  serviceAreaTypes: 'serviceAreaTypes',
  templates: 'templates',
  violationCodeSets: 'violationCodeSets',
  importTemplates: 'importTemplates',
  forms: 'forms',
  emailTemplates: 'emailTemplates',
  reportDefinitions: 'reportDefinitions',
  reportTemplates: 'reportTemplates',
  reportSchedules: 'reportSchedules',
  gridTemplates: 'gridTemplates',
  fieldDefinitions: 'fieldDefinitions',
  emailConfigurations: 'emailConfigurations',
  mailConfigurations: 'mailConfigurations',
  generatedReports: 'generatedReports',
  uploadedFiles: 'uploadedFiles',
  userGroups: 'userGroups',
  feeSchedules: 'feeSchedules',
  // --- Inspection Programs & Entity Scheduling (#737) — tenant-owned ---
  inspectionPrograms: 'inspectionPrograms',
  autoScheduleRules: 'autoScheduleRules',
  carryForwardRules: 'carryForwardRules',

  // --- System collections ---
  organizations: 'organizations',
  tenants: 'tenants',
  featureFlags: 'featureFlags',
  modules: 'modules',
  tiers: 'tiers',
  systemInspectionTemplates: 'systemInspectionTemplates',
  systemForms: 'systemForms',
  systemReportDefinitions: 'systemReportDefinitions',
} as const;

export type AuditCollectionId = (typeof AUDIT_COLLECTIONS)[keyof typeof AUDIT_COLLECTIONS];

/**
 * @contract AUDIT_COLLECTION_ALIASES
 * Purpose: Maps alternate/sync collection names to canonical audit collection IDs.
 * Fields: Key = alternate name (e.g., Dexie table name), Value = canonical AuditCollectionId
 * Rules: Use when the Dexie table name differs from the canonical audit collection name.
 * Sync: N/A — compile-time constant
 */
export const AUDIT_COLLECTION_ALIASES: Record<string, AuditCollectionId> = {
  inspectableEntities: AUDIT_COLLECTIONS.entities,
  inspectionCases: AUDIT_COLLECTIONS.cases,
};

export function resolveAuditCollection(collection: string): string {
  return AUDIT_COLLECTION_ALIASES[collection] ?? collection;
}

export function getAuditCollectionCandidates(collection: string): string[] {
  const canonical = resolveAuditCollection(collection);
  const aliases = Object.entries(AUDIT_COLLECTION_ALIASES)
    .filter(([, value]) => value === canonical)
    .map(([key]) => key);
  return Array.from(new Set([canonical, ...aliases]));
}

export function matchesAuditCollection(left: string, right: string): boolean {
  return resolveAuditCollection(left) === resolveAuditCollection(right);
}
